import { sendLogToServer } from "../../services/logger-service"

const LOG_SOURCE = "In-App Comms"

export interface DeliveryFetchError extends Error {
    status?: number | null
}

// Builds the error stored in state when a delivery fetch fails with an HTTP
// status (or, when there is none, a failure kind such as NETWORK_ERROR). The
// status is kept in both the message and `status`.
export function deliveryHttpError(status: number | null | undefined, problem?: string): DeliveryFetchError {
    const detail = status ? `HTTP ${status}` : (problem || "no response")
    return Object.assign(new Error(`Failed to fetch in-app delivery messages (${detail})`), { status })
}

// API.makeCall rejects with the apisauce response object (not an Error) for any
// non-ok response, so a failed call arrives here as `{ ok: false, status, problem }`.
export function getHttpStatus(err: unknown): number | undefined {
    if (err instanceof Error || typeof err !== "object" || err === null) { return undefined }
    const status = (err as { status?: unknown }).status
    return typeof status === "number" ? status : undefined
}

// Normalises whatever a failed delivery fetch threw or rejected with.
export function toDeliveryFetchError(err: unknown): DeliveryFetchError {
    if (err instanceof Error) { return err }
    if (typeof err === "object" && err !== null) {
        const response = err as { status?: unknown, problem?: unknown }
        return deliveryHttpError(
            typeof response.status === "number" ? response.status : undefined,
            typeof response.problem === "string" ? response.problem : undefined
        )
    }
    return new Error(String(err))
}

// The log message states only the HTTP status or the error text — never
// communication content, user identifiers or tokens.
export function describeDeliveryFailure(error: DeliveryFetchError): string {
    if (error.status) {
        return `In-app communications delivery fetch failed: HTTP ${error.status}`
    }
    return `In-app communications delivery fetch failed: ${error.message || "unknown error"}`
}

// Posts one WARN entry to the SMRT Link server log. Best effort: a rejected or
// not-ok post is ignored — no retry, no queue, no indicator.
export async function logDeliveryFailure(error: DeliveryFetchError): Promise<void> {
    try {
        await sendLogToServer({
            sourceId: LOG_SOURCE,
            level: "WARN",
            message: describeDeliveryFailure(error),
        })
    } catch {
        // intentionally ignored
    }
}
